/**
 * ReadOnlyField.tsx
 * Feature: 116 - Entity Detail Modals
 * Author: Peter Dobler
 * Date: 2026-01-29
 * Constitution Version: 1.12.0
 *
 * Purpose: Display read-only field with label and value for Azure-controlled properties.
 */

import React, { useId, useState } from 'react';
import { Info, Copy, Check } from 'lucide-react';
import { AppTooltip } from '@/components/ui';
import { formatEasternDate } from '@/utils/dateFormatter';

export interface ReadOnlyFieldProps {
  /** Field label */
  label: string;
  /** Field value to display */
  value: React.ReactNode;
  /** Optional tooltip text */
  tooltip?: string;
  /** Whether the value is loading */
  loading?: boolean;
  /** Whether this is a multi-line value */
  multiline?: boolean;
  /** Optional icon to display before value */
  icon?: React.ReactNode;
  /** Whether to use monospace font */
  monospace?: boolean;
  /** Whether to show copy button */
  copyable?: boolean;
  /** Value type for formatting (date displays formatted) */
  type?: 'text' | 'date';
}

/**
 * Format a date string for display
 */
function formatDateValue(value: unknown): string {
  if (!value) return '—';
  const date = new Date(String(value));
  if (isNaN(date.getTime())) return String(value);
  return formatEasternDate(date);
}

/**
 * Whether a value has nothing to show: null, undefined, '' or an empty list.
 */
function isEmptyValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

/**
 * Format a non-date value for display. React renders `true`/`false` as nothing
 * and a string list run together, so a boolean reads Yes/No and a list of
 * strings/numbers reads comma-separated. Anything else (text, numbers, elements)
 * renders as given.
 */
function formatTextValue(value: React.ReactNode): React.ReactNode {
  if (isEmptyValue(value)) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (
    Array.isArray(value) &&
    value.every((item) => typeof item === 'string' || typeof item === 'number')
  ) {
    return value.join(', ');
  }
  return value;
}

/**
 * ReadOnlyField - Displays a read-only field with grey background styling
 */
export const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({
  label,
  value,
  tooltip,
  loading = false,
  multiline = false,
  icon,
  monospace = false,
  copyable = false,
  type = 'text',
}) => {
  const [copied, setCopied] = useState(false);
  const labelId = useId();

  // Format value based on type
  const displayValue = type === 'date' ? formatDateValue(value) : formatTextValue(value);
  const isEmpty = isEmptyValue(value);
  const copyValue = typeof value === 'string' || typeof value === 'number' ? String(value) : '';

  const handleCopy = async () => {
    if (copyValue) {
      await navigator.clipboard.writeText(copyValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      className="mb-4"
      role="group"
      aria-labelledby={labelId}
      aria-busy={loading || undefined}
    >
      <div className="mb-1 flex items-center">
        <span id={labelId} className="text-xs font-medium text-[var(--color-text-secondary)]">
          {label}
        </span>
        {tooltip && (
          <AppTooltip title={tooltip} placement="top">
            <Info className="ml-1 h-3.5 w-3.5 cursor-help text-[var(--color-text-secondary)]" />
          </AppTooltip>
        )}
      </div>

      {loading ? (
        <div
          data-testid="loading-skeleton"
          className="h-4 animate-pulse rounded bg-[var(--color-bg-subtle)]"
          style={{ width: '60%' }}
        />
      ) : (
        <div className="flex items-start gap-2">
          {icon && (
            <span className="mt-0.5 text-[var(--color-text-secondary)]">{icon}</span>
          )}
          <span
            className={[
              'flex-1 text-sm',
              isEmpty
                ? 'italic text-[var(--color-text-secondary)]'
                : 'text-[var(--color-text-primary)]',
              monospace ? 'font-mono text-[0.8rem]' : '',
              multiline
                ? 'whitespace-pre-wrap break-words'
                : 'overflow-hidden text-ellipsis whitespace-nowrap',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {displayValue}
          </span>
          {copyable && copyValue && (
            <AppTooltip title={copied ? 'Copied!' : 'Copy'}>
              <button
                type="button"
                onClick={handleCopy}
                aria-label={`Copy ${label} to clipboard`}
                className="ml-0.5 inline-flex items-center justify-center rounded-[var(--radius-md)] p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] hover:text-[var(--color-text-primary)]"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-[var(--color-status-success)]" />
                ) : (
                  <Copy className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" />
                )}
              </button>
            </AppTooltip>
          )}
        </div>
      )}
    </div>
  );
};

export default ReadOnlyField;
